import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const TechnicalSupportContainer = styled.div`
  padding: 140px 0;
  background: #ffffff;
  min-height: 100vh;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 5%;
`;

const PageTitle = styled.h1`
  text-align: center;
  font-size: 4.5rem;
  color: #2c2c2c;
  margin-bottom: 30px;
  
  letter-spacing: -2px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #2B4B8C, #3B82C4);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const PageSubtitle = styled.p`
  text-align: center;
  font-size: 1.4rem;
  color: #6c757d;
  margin-bottom: 120px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  
`;

// Hero Section
const HeroSection = styled.section`
  position: relative;
  height: 500px;
  border-radius: 25px;
  overflow: hidden;
  margin-bottom: 120px;
  
  &.animate-in {
    animation: ${scaleIn} 0.8s ease-out;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=2126&q=80') no-repeat center center;
    background-size: cover;
    transition: transform 0.5s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(43, 75, 140, 0.85), rgba(59, 130, 196, 0.7));
  }
  
  &:hover::before {
    transform: scale(1.05);
  }
`;

const HeroContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 2;
  color: white;
  
  h2 {
    font-size: 3.5rem;
    
    margin-bottom: 25px;
    letter-spacing: -1.5px;
  }
  
  p {
    font-size: 1.3rem;
    
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;
    opacity: 0.95;
  }
  
  @media (max-width: 768px) {
    h2 {
      font-size: 2.5rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

// Services Section
const ServicesSection = styled.section`
  margin-bottom: 120px;
  
  &.animate-in {
    .services-grid .service-card:nth-child(1) { animation: ${slideInLeft} 0.6s ease-out 0.1s both; }
    .services-grid .service-card:nth-child(2) { animation: ${slideInLeft} 0.6s ease-out 0.3s both; }
    .services-grid .service-card:nth-child(3) { animation: ${slideInLeft} 0.6s ease-out 0.5s both; }
    .services-grid .service-card:nth-child(4) { animation: ${slideInLeft} 0.6s ease-out 0.7s both; }
  }
`;

const SectionTitle = styled.h2`
  font-size: 3.5rem;
  
  color: #2c2c2c;
  margin-bottom: 80px;
  text-align: center;
  letter-spacing: -1.5px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;
  max-width: 1000px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background: white;
  padding: 50px 40px;
  border-radius: 20px;
  border: 1px solid rgba(43, 75, 140, 0.08);
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(180deg, #2B4B8C, #3B82C4);
    transform: scaleY(0);
    transition: transform 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(43, 75, 140, 0.12);
    border-color: rgba(43, 75, 140, 0.15);
  }
  
  &:hover::before {
    transform: scaleY(1);
  }
  
  .icon {
    font-size: 2.5rem;
    color: #2B4B8C;
    margin-bottom: 25px;
    transition: all 0.4s ease;
  }
  
  &:hover .icon {
    transform: scale(1.1);
    color: #3B82C4;
  }
  
  h3 {
    font-size: 1.8rem;
    color: #2c2c2c;
    margin-bottom: 20px;
    font-weight: 500;
    letter-spacing: -0.5px;
  }
  
  p {
    color: #6c757d;
    line-height: 1.8;
    font-size: 1.1rem;
    
    margin-bottom: 20px;
  }
  
  ul {
    margin: 20px 0;
    padding-left: 0;
    list-style: none;
    
    li {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      margin-bottom: 12px;
      color: #6c757d;
      font-size: 1rem;
      line-height: 1.6;
      
      &::before {
        content: '•';
        color: #2B4B8C;
        font-weight: bold;
        margin-top: 2px;
      }
    }
  }
`;

// Contact Section
const ContactSection = styled.section`
  background: linear-gradient(135deg, #2B4B8C 0%, #3B82C4 100%);
  padding: 100px 0;
  border-radius: 30px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
  
  &.animate-in {
    animation: ${fadeInUp} 0.8s ease-out;
  }
  
  h2 {
    font-size: 3rem;
    
    color: white;
    margin-bottom: 25px;
    letter-spacing: -1px;
  }
  
  p {
    font-size: 1.3rem;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.7;
    max-width: 600px;
    margin: 0 auto 40px;
    
  }
  
  .contact-info {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 40px;
    max-width: 800px;
    margin: 40px auto;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 30px;
    }
  }
  
  .contact-item {
    text-align: center;
    
    .icon {
      font-size: 2rem;
      margin-bottom: 15px;
      display: block;
    }
    
    h3 {
      font-size: 1.2rem;
      margin-bottom: 10px;
      color: white;
      font-weight: 500;
    }
    
    p {
      font-size: 1rem;
      margin: 0;
      opacity: 0.9;
    }
  }
  
  .contact-btn {
    background: rgba(255, 255, 255, 0.2);
    border: 2px solid white;
    color: white;
    padding: 15px 40px;
    border-radius: 30px;
    font-weight: 500;
    font-size: 1.1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;
    display: inline-block;
    backdrop-filter: blur(10px);
    letter-spacing: 0.5px;
    margin-top: 20px;
    
    &:hover {
      background: white;
      color: #2B4B8C;
      transform: translateY(-3px);
    }
  }
  
  @media (max-width: 768px) {
    h2 {
      font-size: 2.2rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

const TechnicalSupport = () => {
  const [animatedSections, setAnimatedSections] = useState(new Set());
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedSections(prev => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <TechnicalSupportContainer>
      <Container>
        <PageTitle>{t('technicalSupport.title')}</PageTitle>
        <PageSubtitle>
          {t('technicalSupport.subtitle')}
        </PageSubtitle>

        <HeroSection 
          id="hero-section" 
          data-animate
          className={animatedSections.has('hero-section') ? 'animate-in' : ''}
        >
          <HeroContent>
            <h2>{t('technicalSupport.heroTitle')}</h2>
            <p>
              {t('technicalSupport.heroDesc')}
            </p>
          </HeroContent>
        </HeroSection>

        <ServicesSection 
          id="services-section" 
          data-animate
          className={animatedSections.has('services-section') ? 'animate-in' : ''}
        >
          <SectionTitle>{t('technicalSupport.servicesTitle')}</SectionTitle>
          <ServicesGrid className="services-grid">
            <ServiceCard className="service-card">
              <div className="icon">🔧</div>
              <h3>{t('technicalSupport.srv1Title')}</h3>
              <p>
                {t('technicalSupport.srv1Desc')}
              </p>
              <ul>
                <li>{t('technicalSupport.srv1f1')}</li>
                <li>{t('technicalSupport.srv1f2')}</li>
                <li>{t('technicalSupport.srv1f3')}</li>
                <li>{t('technicalSupport.srv1f4')}</li>
              </ul>
            </ServiceCard>

            <ServiceCard className="service-card">
              <div className="icon">📊</div>
              <h3>{t('technicalSupport.srv2Title')}</h3>
              <p>
                {t('technicalSupport.srv2Desc')}
              </p>
              <ul>
                <li>{t('technicalSupport.srv2f1')}</li>
                <li>{t('technicalSupport.srv2f2')}</li>
                <li>{t('technicalSupport.srv2f3')}</li>
                <li>{t('technicalSupport.srv2f4')}</li>
              </ul>
            </ServiceCard>

            <ServiceCard className="service-card">
              <div className="icon">📱</div>
              <h3>{t('technicalSupport.srv3Title')}</h3>
              <p>
                {t('technicalSupport.srv3Desc')}
              </p>
              <ul>
                <li>{t('technicalSupport.srv3f1')}</li>
                <li>{t('technicalSupport.srv3f2')}</li>
                <li>{t('technicalSupport.srv3f3')}</li>
                <li>{t('technicalSupport.srv3f4')}</li>
              </ul>
            </ServiceCard>

            <ServiceCard className="service-card">
              <div className="icon">🎓</div>
              <h3>{t('technicalSupport.srv4Title')}</h3>
              <p>
                {t('technicalSupport.srv4Desc')}
              </p>
              <ul>
                <li>{t('technicalSupport.srv4f1')}</li>
                <li>{t('technicalSupport.srv4f2')}</li>
                <li>{t('technicalSupport.srv4f3')}</li>
                <li>{t('technicalSupport.srv4f4')}</li>
              </ul>
            </ServiceCard>
          </ServicesGrid>
        </ServicesSection>

        <ContactSection 
          id="contact-section" 
          data-animate
          className={animatedSections.has('contact-section') ? 'animate-in' : ''}
        >
          <h2>{t('technicalSupport.contactTitle')}</h2>
          <p>
            {t('technicalSupport.contactDesc')}
          </p>
          
          <div className="contact-info">
            <div className="contact-item">
              <span className="icon">📞</span>
              <h3>{t('technicalSupport.phone')}</h3>
              <p>+90 532 131 63 30</p>
            </div>
            <div className="contact-item">
              <span className="icon">📧</span>
              <h3>{t('technicalSupport.email')}</h3>
              <p>teknik@dmrcimento.com</p>
            </div>
            <div className="contact-item">
              <span className="icon">⏰</span>
              <h3>{t('technicalSupport.hours')}</h3>
              <p>{t('technicalSupport.hoursDesc')}</p>
            </div>
          </div>
          
          <a href="/iletisim" className="contact-btn">
            {t('technicalSupport.contactBtn')}
          </a>
        </ContactSection>
      </Container>
    </TechnicalSupportContainer>
  );
};

export default TechnicalSupport;
